import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/Avatar';
import { PostCard } from '@/components/PostCard';
import { FloorTheme, getTheme } from '@/lib/themes';
import api, { getSpotlightStatus, getUserPosts, getUserProfile } from '@/lib/api';
import { Loader2, UserPlus } from 'lucide-react';

export function UserProfile() {
    const { theme, user: currentUser } = useOutletContext<{ theme: FloorTheme; user: any }>();
    const { userId } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<any>(null);
    const [posts, setPosts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sendingRequest, setSendingRequest] = useState(false);
    const [requestMessage, setRequestMessage] = useState('');
    const [requestError, setRequestError] = useState('');
    const [requestStatus, setRequestStatus] = useState<'idle' | 'sent' | 'friends'>('idle');
    const [spotlightStatus, setSpotlightStatus] = useState<{ spotlight_post_id?: number | null; self_spotlight_post_id?: number | null } | null>(null);

    const parsedUserId = Number(userId);
    const isSelf = currentUser?.user_id && parsedUserId === currentUser.user_id;

    const formatFloorLabel = (roleType?: string, floorLevel?: number) => {
        if (roleType && ['Admin', 'Manager', 'Maestro'].includes(roleType)) return '∞';
        if (typeof floorLevel === 'number') return String(floorLevel);
        return '-';
    };

    useEffect(() => {
        if (!userId || Number.isNaN(parsedUserId)) {
            setError('ユーザーが見つかりません。');
            setLoading(false);
            return;
        }

        const fetchProfile = async () => {
            setLoading(true);
            setError('');
            try {
                const [profileRes, postsRes] = await Promise.all([
                    getUserProfile(parsedUserId),
                    getUserPosts(parsedUserId),
                ]);
                setProfile(profileRes.data);
                setPosts(postsRes.data || []);
            } catch (err: any) {
                setError(err?.response?.data?.detail || 'プロフィールの読み込みに失敗しました。');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [userId, parsedUserId]);

    const refreshSpotlightStatus = async () => {
        try {
            const res = await getSpotlightStatus();
            setSpotlightStatus(res.data);
        } catch (err) {
            console.error('Failed to fetch spotlight status', err);
        }
    };

    useEffect(() => {
        if (!currentUser) return;
        refreshSpotlightStatus();
    }, [currentUser]);

    const handleSendRequest = async () => {
        if (!profile?.user_id) return;
        setSendingRequest(true);
        setRequestError('');
        setRequestMessage('');
        try {
            const res = await api.post('/friends/requests', { receiver_id: profile.user_id });
            const message = res?.data?.message;
            if (message?.toLowerCase().includes('already friends')) {
                setRequestStatus('friends');
                setRequestMessage('すでに隣人です。');
            } else {
                setRequestStatus('sent');
                setRequestMessage('挨拶を送信しました。');
            }
        } catch (err: any) {
            setRequestError(err?.response?.data?.detail || '挨拶の送信に失敗しました。');
        } finally {
            setSendingRequest(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-[60vh] items-center justify-center">
                <Loader2 className="animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-3xl mx-auto space-y-4">
                <div className="p-4 rounded-lg border bg-white/80 text-sm text-red-600" style={{ borderColor: theme.colors.border }}>
                    {error}
                </div>
                <Button variant="outline" onClick={() => navigate(-1)}>
                    戻る
                </Button>
            </div>
        );
    }

    if (!profile) return null;

    const displayName = profile.display_name?.trim() || '住人';
    const introText = profile.intro_text || '自己紹介はまだありません。';

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-serif font-bold">住人プロフィール</h1>
                    <p className="text-sm opacity-70">投稿をクリックして移動できます。</p>
                </div>
                <Button variant="outline" onClick={() => navigate(-1)}>
                    戻る
                </Button>
            </div>

            <div
                className="p-6 rounded-xl shadow-sm border backdrop-blur"
                style={{
                    backgroundColor: `${theme.colors.card}E6`,
                    borderColor: theme.colors.border,
                    color: theme.colors.text,
                }}
            >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Avatar
                            username={displayName}
                            src={profile.avatar_path}
                            size="xl"
                            theme={getTheme(profile.role_type, profile.floor_level)}
                        />
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold">{displayName}</h2>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                    F{formatFloorLabel(profile.role_type, profile.floor_level)}
                                </span>
                            </div>
                            <div className="text-xs opacity-60">RoomNo. {profile.room_no || '-'}</div>
                        </div>
                    </div>

                    {!isSelf && (
                        <div className="flex flex-col items-start gap-2">
                            <Button
                                onClick={handleSendRequest}
                                disabled={sendingRequest || requestStatus !== 'idle'}
                                className="gap-2"
                                style={{ backgroundColor: theme.colors.primary, color: '#fff' }}
                            >
                                <UserPlus size={16} />
                                {requestStatus === 'friends' ? '隣人' : requestStatus === 'sent' ? '送信済み' : '挨拶する'}
                            </Button>
                            {requestMessage && <div className="text-xs text-green-700">{requestMessage}</div>}
                            {requestError && <div className="text-xs text-red-600">{requestError}</div>}
                        </div>
                    )}
                </div>

                <div className="mt-5 rounded-lg border bg-white/70 p-4" style={{ borderColor: theme.colors.border }}>
                    <div className="text-sm font-semibold mb-2">自己紹介</div>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{introText}</div>
                </div>
            </div>

            <div className="space-y-4">
                <div className="text-sm font-semibold">投稿一覧</div>
                {posts.length === 0 ? (
                    <div className="text-sm opacity-70">まだ投稿がありません。</div>
                ) : (
                    posts.map((post) => (
                        <PostCard
                            key={post.post_id}
                            post={post}
                            currentUserId={currentUser?.user_id}
                            currentUser={currentUser}
                            spotlightPostId={spotlightStatus?.spotlight_post_id ?? null}
                            selfSpotlightPostId={spotlightStatus?.self_spotlight_post_id ?? null}
                            onSpotlightChange={refreshSpotlightStatus}
                        />
                    ))
                )}
            </div>
        </div>
    );
}
